import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { orderApi, customerApi, paymentApi, shopSettingsApi as settingsApi } from '../services/api';
import { Order, Customer, Payment, ApiResponse } from '../types';
import { ORDER_STATUS, APP_CONFIG, PAYMENT_METHODS, PAYMENT_TYPES } from '../utils/constants';

const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Status update
  const [statusUpdateDialog, setStatusUpdateDialog] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Payment confirmation dialog for delivery with balance due
  const [paymentConfirmDialog, setPaymentConfirmDialog] = useState(false);
  const [pendingBalanceAmount, setPendingBalanceAmount] = useState<number>(0);
  
  // Shop settings for print
  const [shopSettings, setShopSettings] = useState<any>(null);

  useEffect(() => {
    if (id) {
      loadOrder(parseInt(id));
      loadShopSettings();
    }
  }, [id]);

  const loadOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const response = await orderApi.getById(orderId);
      if (response.status === 'success' && response.data) {
        setOrder(response.data);
        if (response.data.customer_id) {
          loadCustomer(response.data.customer_id);
        }
        // Load payments for this order
        loadPayments(orderId);
      } else {
        setError(response.message || 'Failed to load order');
      }
    } catch (err) {
      setError('Error loading order');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async (orderId: number) => {
    try {
      const response = await paymentApi.getByOrder(orderId);
      if (response.status === 'success' && response.data) {
        setPayments(response.data || []);
      }
    } catch (err) {
      console.error('Error loading payments:', err);
    }
  };

  const loadShopSettings = async () => {
    try {
      const response = await settingsApi.getAll();
      if (response.status === 'success' && response.data) {
        setShopSettings(response.data);
      }
    } catch (err) {
      console.error('Error loading shop settings:', err);
    }
  };

  const loadCustomer = async (customerId: number) => {
    try {
      const response = await customerApi.getById(customerId);
      if (response.status === 'success' && response.data) {
        setCustomer(response.data);
      }
    } catch (err) {
      console.error('Error loading customer:', err);
    }
  };

  const handleStatusUpdate = async () => {
    if (!order || !newStatus) return;
    
    // Check if trying to set status to delivered with balance due
    if (newStatus === 'delivered') {
      const balanceAmount = parseFloat(order.balance_amount?.toString() || '0');
      if (balanceAmount > 0) {
        // Show confirmation dialog instead of error
        setPendingBalanceAmount(balanceAmount);
        setPaymentConfirmDialog(true);
        return;
      }
    }
    
    // Proceed with status update if no balance due
    await updateOrderStatus(newStatus);
  };

  const handlePaymentConfirm = async () => {
    if (!order) return;
    
    try {
      setUpdatingStatus(true);
      setError(null);
      
      // Create payment for the balance amount
      const paymentData = {
        order_id: order.id!,
        amount: pendingBalanceAmount.toFixed(2),
        payment_type: 'balance' as const,
        payment_method: 'cash' as const,
        payment_date: new Date().toISOString().split('T')[0], // Format: YYYY-MM-DD
        notes: 'Payment collected on delivery'
      };
      
      console.log('Creating payment:', paymentData);
      
      // Create payment first
      const paymentResponse = await paymentApi.create(paymentData);
      
      console.log('Payment response:', paymentResponse);
      
      if (paymentResponse.status === 'success') {
        // Reload payments to get updated list
        await loadPayments(order.id!);
        // Then update status to delivered
        await updateOrderStatus('delivered');
        // Close confirmation dialog
        setPaymentConfirmDialog(false);
        setPendingBalanceAmount(0);
      } else {
        setError(paymentResponse.message || 'Failed to record payment');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Error processing payment and status update';
      setError(errorMessage);
      console.error('Payment confirmation error:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updateOrderStatus = async (status: string) => {
    if (!order) return;
    
    try {
      setUpdatingStatus(true);
      setError(null);
      const response = await orderApi.updateStatus(order.id!, status);
      if (response.status === 'success') {
        // Reload order to get updated balance and payment info
        await loadOrder(order.id!);
        setStatusUpdateDialog(false);
        setNewStatus('');
      } else {
        setError(response.message || 'Failed to update status');
      }
    } catch (err) {
      setError('Error updating order status');
      console.error(err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'default';
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'warning'; // Yellow
      case 'delivered': return 'success'; // Green
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    if (!status) return 'UNKNOWN';
    return status.replace('_', ' ').toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };


  const printCustomerCopy = () => {
    if (!order || !customer) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order ${order.order_number || order.id} - Customer Copy</title>
        <style>
          @page { 
            size: A4 landscape; 
            margin: 0; 
          }
          @media print {
            @page { 
              size: A4 landscape; 
              margin: 0; 
            }
            body { 
              width: 5in; 
              height: 8in; 
              margin: 0; 
              padding: 0.25in; 
              transform: translate(0.5in, 0.5in); 
            }
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 10px; 
            width: 4.5in; 
            height: 7.5in; 
            font-size: 12px; 
            border: 2px solid #333; 
            position: relative; 
          }
          .header { text-align: center; margin-bottom: 15px; }
          .shop-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
          .shop-details { font-size: 10px; color: #666; }
          .order-info { margin-bottom: 10px; }
          .order-info h3 { margin: 0 0 5px 0; font-size: 14px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 3px; font-size: 11px; }
          .items-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10px; padding-bottom: 80px; }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 4px; text-align: left; }
          .items-table th { background-color: #f5f5f5; font-size: 10px; }
          .amounts { 
            position: absolute; 
            bottom: 10px; 
            left: 10px; 
            right: 10px; 
            padding: 10px; 
            background: white; 
            border-top: 1px solid #ddd; 
          }
          .amount-row { display: flex; justify-content: space-between; margin-bottom: 3px; font-size: 11px; }
          .total-row { font-weight: bold; font-size: 12px; border-top: 1px solid #333; padding-top: 5px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="shop-name">${shopSettings?.shop_name || 'Tailor Shop'}</div>
          <div class="shop-details">
            ${shopSettings?.shop_address || ''}<br>
            ${shopSettings?.shop_phone || ''}<br>
            ${shopSettings?.shop_email || ''}
          </div>
        </div>
        
        <div class="order-info">
          <h3>Order Details</h3>
          <div class="info-row">
            <span>Order Number:</span>
            <span>${order.order_number || order.id}</span>
          </div>
          <div class="info-row">
            <span>Customer:</span>
            <span>${customer.name}</span>
          </div>
          <div class="info-row">
            <span>Order Date:</span>
            <span>${new Date(order.order_date).toLocaleDateString()}</span>
          </div>
          <div class="info-row">
            <span>Delivery Date:</span>
            <span>${order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : 'Not set'}</span>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${order.items?.map(item => `
              <tr>
                <td>${item.stitch_item_name || 'Item'}</td>
                <td>${item.quantity}</td>
                <td>${APP_CONFIG.CURRENCY}${item.unit_price}</td>
                <td>${APP_CONFIG.CURRENCY}${item.total_price}</td>
              </tr>
            `).join('') || ''}
          </tbody>
        </table>

        <div class="amounts">
          <div class="amount-row">
            <span>Total Amount:</span>
            <span>${APP_CONFIG.CURRENCY}${order.total_amount}</span>
          </div>
          <div class="amount-row">
            <span>Advance Paid:</span>
            <span>${APP_CONFIG.CURRENCY}${order.advance_amount}</span>
          </div>
          <div class="amount-row total-row">
            <span>Balance Due:</span>
            <span>${APP_CONFIG.CURRENCY}${order.balance_amount}</span>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const printStaffCopy = () => {
    if (!order || !customer) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order ${order.order_number || order.id} - Staff Copy</title>
        <style>
          @page { 
            size: A4 landscape; 
            margin: 0; 
          }
          @media print {
            @page { 
              size: A4 landscape; 
              margin: 0; 
            }
            body { 
              width: 5in; 
              height: 8in; 
              margin: 0; 
              padding: 0.25in; 
              transform: translate(0.5in, 0.5in); 
            }
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 10px; 
            width: 4.5in; 
            height: 7.5in; 
            font-size: 12px; 
            border: 2px solid #333; 
            position: relative; 
          }
          .header { text-align: center; margin-bottom: 15px; }
          .shop-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
          .shop-details { font-size: 10px; color: #666; }
          .order-info { margin-bottom: 10px; }
          .order-info h3 { margin: 0 0 5px 0; font-size: 14px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 3px; font-size: 11px; }
          .items-section { margin-bottom: 10px; }
          .item-card { border: 1px solid #ddd; margin-bottom: 8px; padding: 8px; }
          .item-header { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .item-name { font-weight: bold; font-size: 12px; }
          .measurements { margin-top: 5px; }
          .measurement-item { display: inline-block; background: #f5f5f5; padding: 2px 6px; margin: 1px; border-radius: 2px; font-size: 9px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="shop-name">${shopSettings?.shop_name || 'Tailor Shop'}</div>
          <div class="shop-details">
            ${shopSettings?.shop_address || ''}<br>
            ${shopSettings?.shop_phone || ''}<br>
            ${shopSettings?.shop_email || ''}
          </div>
        </div>
        
        <div class="order-info">
          <h3>Order Details</h3>
          <div class="info-row">
            <span>Order Number:</span>
            <span>${order.order_number || order.id}</span>
          </div>
          <div class="info-row">
            <span>Customer:</span>
            <span>${customer.name}</span>
          </div>
          <div class="info-row">
            <span>Order Date:</span>
            <span>${new Date(order.order_date).toLocaleDateString()}</span>
          </div>
          <div class="info-row">
            <span>Delivery Date:</span>
            <span>${order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : 'Not set'}</span>
          </div>
        </div>

        <div class="items-section">
          <h3>Order Items with Measurements</h3>
          ${order.items?.map(item => `
            <div class="item-card">
              <div class="item-header">
                <div class="item-name">${item.stitch_item_name || 'Item'}</div>
                <div>Qty: ${item.quantity}</div>
              </div>
              ${item.special_instructions ? `<div style="margin-bottom: 10px; font-style: italic;">${item.special_instructions}</div>` : ''}
              <div class="measurements">
                ${item.measurements_data && item.measurements_data.length > 0 ? 
                  item.measurements_data.map(measurement => `
                    <span class="measurement-item">
                      ${measurement.measurement_name}: ${measurement.measurement_value} ${measurement.unit}
                    </span>
                  `).join('') : 
                  '<span style="color: #666; font-style: italic;">No measurements provided</span>'
                }
              </div>
            </div>
          `).join('') || ''}
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // WhatsApp functions
  const generatePDFContent = (type: 'customer' | 'staff') => {
    if (!order || !customer) return '';
    
    const title = type === 'customer' ? 'Customer Copy' : 'Staff Copy';
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order ${order.order_number || order.id} - ${title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .order-info { margin-bottom: 20px; }
          .customer-info, .order-items { margin-bottom: 20px; }
          .item-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
          .total { font-weight: bold; font-size: 18px; margin-top: 20px; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Order Receipt - ${title}</h1>
          <h2>Order #${order.order_number || order.id}</h2>
        </div>
        
        <div class="order-info">
          <p><strong>Order Date:</strong> ${order.created_at ? new Date(order.created_at).toLocaleDateString() : 'Not available'}</p>
          <p><strong>Status:</strong> ${order.status}</p>
          <p><strong>Delivery Date:</strong> ${order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : 'Not set'}</p>
        </div>
        
        <div class="customer-info">
          <h3>Customer Information</h3>
          <p><strong>Name:</strong> ${customer.name}</p>
          <p><strong>Phone:</strong> ${customer.phone}</p>
          ${customer.email ? `<p><strong>Email:</strong> ${customer.email}</p>` : ''}
          ${customer.address ? `<p><strong>Address:</strong> ${customer.address}</p>` : ''}
        </div>
        
        <div class="order-items">
          <h3>Order Items</h3>
          ${order.items.map(item => `
            <div class="item-row">
              <span>${item.stitch_item_name || 'Item'} (${item.quantity}x)</span>
              <span>₹${item.unit_price}</span>
            </div>
          `).join('')}
          <div class="item-row">
            <span><strong>Total Amount:</strong></span>
            <span><strong>₹${order.total_amount}</strong></span>
          </div>
        </div>
        
        <div class="footer">
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    return content;
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box>
        <Alert severity="error">Order not found</Alert>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/orders')}
          sx={{ mt: 2 }}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/orders')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
        </Box>
        
        <Box>
          {order.status !== 'delivered' && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/orders/${order.id}/edit`)}
              sx={{ mr: 1 }}
            >
              Edit
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={printCustomerCopy}
            sx={{ mr: 1 }}
          >
            Print Customer Copy
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={printStaffCopy}
            sx={{ mr: 1 }}
          >
            Print Staff Copy
          </Button>
          <Button
            variant="contained"
            onClick={() => setStatusUpdateDialog(true)}
          >
            Update Status
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Order Information */}
        <Box sx={{ flex: '1 1 600px', minWidth: '500px' }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Information
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Order Number
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  #{order.order_number || order.id}
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={getStatusLabel(order?.status)}
                  color={getStatusColor(order?.status) as any}
                  size="small"
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Order Date
                </Typography>
                <Typography variant="subtitle1">
                  {formatDate(order.order_date)}
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Delivery Date
                </Typography>
                <Typography variant="subtitle1">
                  {order.delivery_date ? formatDate(order.delivery_date) : 'Not specified'}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Order Items */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Items
            </Typography>
            <Box>
              {order.items?.map((item, index) => (
                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {/* Item Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {item.stitch_item_name || `Item ${index + 1}`}
                      </Typography>
                      {item.special_instructions && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                          {item.special_instructions}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" color="primary">
                        {APP_CONFIG.CURRENCY}{item.total_price}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.quantity} × {APP_CONFIG.CURRENCY}{item.unit_price}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Measurements Section */}
                  {item.measurements_data && item.measurements_data.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                        Measurements:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {item.measurements_data.map((measurement, measurementIndex) => (
                          <Box 
                            key={measurementIndex} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              bgcolor: 'grey.50',
                              px: 2,
                              py: 1,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.200'
                            }}
                          >
                            <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                              {measurement.measurement_name}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {measurement.measurement_value} {measurement.unit}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      No measurements provided
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>


          {/* Notes */}
          {order.notes && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body1">
                {order.notes}
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          {/* Customer Information */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            {customer && (
              <Box>
                <Box display="flex" alignItems="center" mb={2}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {customer.name}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" mb={1}>
                  <PhoneIcon sx={{ mr: 1, fontSize: 'small' }} />
                  <Typography variant="body2">{customer.phone}</Typography>
                </Box>
                
                {customer.email && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <EmailIcon sx={{ mr: 1, fontSize: 'small' }} />
                    <Typography variant="body2">{customer.email}</Typography>
                  </Box>
                )}
                
                {customer.address && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <LocationIcon sx={{ mr: 1, fontSize: 'small' }} />
                    <Typography variant="body2">{customer.address}</Typography>
                  </Box>
                )}
                
              </Box>
            )}
          </Paper>

          {/* Payment Summary */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Total Amount:</Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {APP_CONFIG.CURRENCY}{order.total_amount}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Total Paid:
                </Typography>
                <Typography variant="subtitle1" color="success.main" fontWeight="bold">
                  {APP_CONFIG.CURRENCY}{(() => {
                    const totalPaid = payments.reduce((sum, payment) => {
                      return sum + parseFloat(payment.amount || '0');
                    }, 0);
                    return totalPaid.toFixed(2);
                  })()}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight="bold">
                  Balance Due:
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  color={parseFloat(order.balance_amount || '0') > 0 ? 'error.main' : 'success.main'} 
                  fontWeight="bold"
                >
                  {APP_CONFIG.CURRENCY}{order.balance_amount}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateDialog} onClose={() => setStatusUpdateDialog(false)}>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              label="New Status"
              onChange={(e: SelectChangeEvent) => setNewStatus(e.target.value)}
            >
              {Object.values(ORDER_STATUS).map(status => (
                <MenuItem key={status} value={status}>
                  {getStatusLabel(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleStatusUpdate} 
            variant="contained"
            disabled={updatingStatus}
          >
            {updatingStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Confirmation Dialog for Delivery with Balance Due */}
      <Dialog open={paymentConfirmDialog} onClose={() => setPaymentConfirmDialog(false)}>
        <DialogTitle>Collect Balance Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you collected the due amount of{' '}
            <strong>{APP_CONFIG.CURRENCY}{pendingBalanceAmount.toFixed(2)}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Clicking "Yes" will:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Record a payment of {APP_CONFIG.CURRENCY}{pendingBalanceAmount.toFixed(2)}</li>
              <li>Update order status to "Delivered"</li>
              <li>Set balance due to {APP_CONFIG.CURRENCY}0.00</li>
            </ul>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setPaymentConfirmDialog(false);
              setPendingBalanceAmount(0);
            }}
          >
            No
          </Button>
          <Button 
            onClick={handlePaymentConfirm} 
            variant="contained"
            color="primary"
            disabled={updatingStatus}
          >
            {updatingStatus ? 'Processing...' : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderDetails; 